import React from "react";
import { useTranslation } from "react-i18next";
import { useAppSelector } from "../../../../../common/hooks/hooks";
import ProductCard from "../common/ProductCard/ProductCard";
import { TransformedProduct } from "../../types/productApiTypes";
import ProductsGridSkeleton from "./ProductsGridSkeleton";
import Icon from "../../../../../common/components/Icon/Icon";

interface ProductsGridProps {
  products?: TransformedProduct[];
  isLoading: boolean;
  isFetching: boolean;
  isError: boolean;
  onQuickViewClick: (productId: number) => void;
}

const ProductsGrid: React.FC<ProductsGridProps> = ({
  products,
  isLoading,
  isFetching,
  isError,
  onQuickViewClick,
}) => {
  const { t, ready: areTranslationsReady } = useTranslation();
  const activeView = useAppSelector((state) => state.product.ui.activeView);

  // State 1: Centralized Loading State -> Show Skeleton
  // The component is considered "loading" if either the API is fetching initial data
  // OR the translation files haven't loaded yet. This prevents a flash of untranslated content.
  const isComponentLoading = isLoading || !areTranslationsReady;

  if (isComponentLoading) {
    return (
      <div className="products-grid-wrapper">
        <ProductsGridSkeleton />
      </div>
    );
  }

  // State 2: API Error (will only show if there's an error and not loading)
  if (isError) {
    return (
      <div className="products-grid-wrapper">
        <div className="products-grid-wrapper__message products-grid-wrapper__message--error">
          <Icon iconName="x-mark" className="products-grid-wrapper__icon" />
          {t("productsArchive.grid.errorText")}
        </div>
      </div>
    );
  }

  // State 3: Success, but no products found (will only show if not loading and no error)
  if (!products?.length) {
    return (
      <div className="products-grid-wrapper">
        <div className="products-grid-wrapper__message products-grid-wrapper__message--info">
          <Icon
            iconName="not-allowed"
            className="products-grid-wrapper__icon"
          />
          {t("productsArchive.grid.noResultsText")}
        </div>
      </div>
    );
  }

  // State 5: Success with products (and handle refetching overlay)
  return (
    <div className="products-grid-wrapper">
      {/* State 4: This overlay will only show when refetching (isFetching is true, but isLoading is false) */}
      {isFetching && (
        <div className="products-grid-wrapper__loader-overlay">
          <div className="loader"></div>
        </div>
      )}
      <div className="products-grid">
        {products.map((p) => (
          <ProductCard
            key={p.id}
            product={p}
            view={activeView}
            onQuickViewClick={onQuickViewClick}
          />
        ))}
      </div>
    </div>
  );
};

export default ProductsGrid;
